import { useState, useEffect } from "react";
import { Cookie, X, Settings, CheckCircle } from "lucide-react";
import { Link } from "react-router-dom";

type ConsentState = "pending" | "accepted" | "rejected" | "custom";

export default function CookieBanner() {
  const [state, setState] = useState<ConsentState>("pending");
  const [showDetails, setShowDetails] = useState(false);
  const [analytics, setAnalytics] = useState(true);
  const [advertising, setAdvertising] = useState(true);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem("cookie_consent");
    if (saved) {
      setState(saved as ConsentState);
    } else {
      // Small delay so it animates in
      requestAnimationFrame(() => setVisible(true));
    }
  }, []);

  const save = (val: ConsentState) => {
    localStorage.setItem("cookie_consent", val);
    setVisible(false);
    setTimeout(() => setState(val), 300);
    if (typeof window !== "undefined" && (window as unknown as { gtag?: (...args: unknown[]) => void }).gtag) {
      const w = window as unknown as { gtag: (...args: unknown[]) => void };
      const consent = val === "accepted" || (val === "custom" && advertising);
      w.gtag("consent", "update", {
        analytics_storage: val === "rejected" ? "denied" : "granted",
        ad_storage: consent ? "granted" : "denied",
        ad_personalization: consent ? "granted" : "denied",
        ad_user_data: consent ? "granted" : "denied",
      });
    }
  };

  if (state !== "pending") return null;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-[100] shadow-hero transition-all duration-300 ease-out"
      style={{
        background: "hsl(var(--secondary))",
        borderTop: "1px solid hsl(0 0% 100% / 0.1)",
        transform: visible ? "translateY(0)" : "translateY(100%)",
        opacity: visible ? 1 : 0,
      }}
      role="dialog"
      aria-label="Configuración de cookies"
    >
      <div className="container mx-auto px-4 sm:px-6 py-4 md:py-5">
        {!showDetails ? (
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
            <div className="flex items-start gap-3 flex-1">
              <div
                className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
                style={{ background: "hsl(var(--accent) / 0.15)" }}
              >
                <Cookie className="w-5 h-5" style={{ color: "hsl(var(--accent))" }} />
              </div>
              <div>
                <p className="text-sm font-semibold mb-1" style={{ color: "hsl(0 0% 95%)" }}>
                  Usamos cookies para mejorar tu experiencia
                </p>
                <p className="text-xs leading-relaxed" style={{ color: "hsl(215 20% 60%)" }}>
                  Utilizamos cookies propias y de terceros (incluyendo Google AdSense) para mostrar
                  publicidad relevante y analizar el tráfico. Puedes aceptar todas, rechazar las no
                  esenciales o personalizar tu elección.{" "}
                  <Link to="/politica-de-cookies" className="underline hover:opacity-80" style={{ color: "hsl(var(--accent))" }}>
                    Política de Cookies
                  </Link>
                  {" · "}
                  <Link to="/politica-de-privacidad" className="underline hover:opacity-80" style={{ color: "hsl(var(--accent))" }}>
                    Política de Privacidad
                  </Link>
                </p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 w-full md:w-auto flex-shrink-0">
              <button
                onClick={() => setShowDetails(true)}
                className="flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium border transition-all hover:bg-white/10"
                style={{ color: "hsl(215 20% 70%)", borderColor: "hsl(0 0% 100% / 0.15)" }}
              >
                <Settings className="w-3.5 h-3.5" />
                Personalizar
              </button>
              <button
                onClick={() => save("rejected")}
                className="flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium border transition-all hover:bg-white/10"
                style={{ color: "hsl(215 20% 70%)", borderColor: "hsl(0 0% 100% / 0.15)" }}
              >
                <X className="w-3.5 h-3.5" />
                Solo esenciales
              </button>
              <button onClick={() => save("accepted")} className="btn-cta px-5 py-2 text-xs">
                <CheckCircle className="w-3.5 h-3.5" />
                Aceptar todo
              </button>
            </div>
          </div>
        ) : (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-sm" style={{ color: "hsl(0 0% 95%)" }}>
                Personalizar preferencias de cookies
              </h3>
              <button onClick={() => setShowDetails(false)} style={{ color: "hsl(215 20% 55%)" }}>
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="space-y-3 mb-4">
              <CookieToggle title="Cookies esenciales" desc="Necesarias para el funcionamiento básico del sitio. No se pueden desactivar." checked={true} disabled={true} onChange={() => {}} />
              <CookieToggle title="Cookies de análisis" desc="Nos ayudan a entender cómo los usuarios interactúan con el sitio (Google Analytics)." checked={analytics} onChange={setAnalytics} />
              <CookieToggle title="Cookies publicitarias" desc="Permiten mostrar anuncios relevantes a través de Google AdSense." checked={advertising} onChange={setAdvertising} />
            </div>
            <div className="flex flex-col sm:flex-row gap-2 justify-end">
              <button onClick={() => save("rejected")} className="px-4 py-2 rounded-lg text-xs font-medium border transition-all hover:bg-white/10" style={{ color: "hsl(215 20% 65%)", borderColor: "hsl(0 0% 100% / 0.15)" }}>
                Rechazar todo
              </button>
              <button onClick={() => save("custom")} className="btn-cta px-5 py-2 text-xs">
                Guardar preferencias
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function CookieToggle({ title, desc, checked, disabled = false, onChange }: {
  title: string; desc: string; checked: boolean; disabled?: boolean; onChange: (v: boolean) => void;
}) {
  return (
    <div className="flex items-start gap-3 p-3 rounded-lg" style={{ background: "hsl(0 0% 100% / 0.04)", border: "1px solid hsl(0 0% 100% / 0.07)" }}>
      <div className="flex-1">
        <p className="text-xs font-semibold mb-0.5" style={{ color: "hsl(0 0% 90%)" }}>{title}</p>
        <p className="text-xs" style={{ color: "hsl(215 20% 55%)" }}>{desc}</p>
      </div>
      <button
        onClick={() => !disabled && onChange(!checked)}
        disabled={disabled}
        className={`relative w-10 h-5 rounded-full transition-all flex-shrink-0 mt-0.5 ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
        style={{ background: checked ? "hsl(var(--accent))" : "hsl(215 20% 25%)" }}
        aria-checked={checked}
        role="switch"
      >
        <span className="absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all" style={{ left: checked ? "calc(100% - 1.125rem)" : "0.125rem" }} />
      </button>
    </div>
  );
}
